import { LineChart, type ChartDataPoint } from '@zyxantech/axy'
import { Card, PageHeader, Skeleton, StatusDot } from '@zyxantech/tux'
import { Activity, AlertTriangle, Cpu, Layers } from 'lucide-react'
import { type ReactNode, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGetAlarmEventsQuery } from '@/api/alarms.api'
import { useGetAssetsQuery } from '@/api/assets.api'
import { useGetGatewaysQuery, type Gateway } from '@/api/gateways.api'
import { useGetTagHistoryQuery, useGetTagsQuery, type Tag } from '@/api/tags.api'
import { AlarmSeverityBadge } from '@/components/alarms/AlarmSeverityBadge'
import { EmptyState } from '@/components/shared/EmptyState'
import { PAGINATION } from '@/constants/pagination'
import { ROUTES } from '@/constants/routes'
import { TIME } from '@/constants/time'
import { useLiveTagValue } from '@/hooks/useLiveTagValue'
import { formatRelativeTime, formatValue } from '@/utils/format'

export function OverviewPage() {
  return (
    <div className="space-y-6 p-6">
      <PageHeader title="Overview" subtitle="Plant operations at a glance" />
      <KpiRow />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <RecentAlarmsPanel />
        <LiveChartPanel />
      </div>
      <GatewayListPanel />
    </div>
  )
}

/* ─── KPI Row ─────────────────────────────────────────────────────────────── */

function KpiRow() {
  const { data: gateways } = useGetGatewaysQuery()
  const { data: alarms } = useGetAlarmEventsQuery({
    status: 'active',
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })
  const { data: tags } = useGetTagsQuery({ page: 1, pageSize: 1 })
  const { data: assets } = useGetAssetsQuery({ page: 1, pageSize: 1 })

  const gwItems = gateways?.items ?? []
  const onlineCount = gwItems.filter((g) => g.status === 'online').length
  const offlineCount = gwItems.filter((g) => g.status !== 'online').length
  const alarmCount = alarms?.pagination.total ?? 0
  const tagCount = tags?.pagination.total ?? 0
  const assetCount = assets?.pagination.total ?? 0

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <KpiCard
        icon={<Cpu size={18} className="text-success" />}
        label="Gateways"
        primary={String(gwItems.length)}
        detail={`${onlineCount} online · ${offlineCount} offline`}
      />
      <KpiCard
        icon={
          <AlertTriangle
            size={18}
            className={alarmCount > 0 ? 'text-danger' : 'text-text-muted'}
          />
        }
        label="Active Alarms"
        primary={String(alarmCount)}
        detail={alarmCount > 0 ? 'Attention required' : 'All clear'}
      />
      <KpiCard
        icon={<Activity size={18} className="text-accent" />}
        label="Tags"
        primary={String(tagCount)}
        detail="Data points"
      />
      <KpiCard
        icon={<Layers size={18} className="text-info" />}
        label="Assets"
        primary={String(assetCount)}
        detail="Tracked assets"
      />
    </div>
  )
}

function KpiCard({
  icon,
  label,
  primary,
  detail,
}: {
  icon: ReactNode
  label: string
  primary: string
  detail: string
}) {
  return (
    <Card padding="md">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-xs text-text-muted">{label}</span>
      </div>
      <p className="mt-2 text-3xl font-bold tabular-nums text-text-primary">{primary}</p>
      <p className="mt-0.5 text-xs text-text-secondary">{detail}</p>
    </Card>
  )
}

/* ─── Recent Alarms ───────────────────────────────────────────────────────── */

function RecentAlarmsPanel() {
  const navigate = useNavigate()
  const { data, isLoading } = useGetAlarmEventsQuery(
    {
      status: 'active',
      page: PAGINATION.DEFAULT_PAGE,
      pageSize: 5,
    },
    { pollingInterval: TIME.ALARM_REFRESH_MS },
  )

  const { data: tagsData } = useGetTagsQuery({
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })

  const tagName = useMemo(() => {
    const m = new Map<string, string>()
    for (const t of tagsData?.items ?? []) m.set(t.id, t.name)
    return m
  }, [tagsData])

  const alarms = data?.items ?? []

  return (
    <Card padding="md">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">Active Alarms</h2>
        <button
          type="button"
          onClick={() => navigate(ROUTES.ALARMS)}
          className="text-xs text-accent hover:underline"
        >
          View all
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={36} />
          ))}
        </div>
      ) : alarms.length === 0 ? (
        <EmptyState title="All clear" message="No active alarms." />
      ) : (
        <div className="divide-y divide-border-subtle">
          {alarms.map((alarm) => (
            <div key={alarm.id} className="flex items-center gap-3 py-2.5">
              <AlarmSeverityBadge severity={alarm.severity} />
              <div className="min-w-0">
                <p className="truncate text-sm text-text-primary">{alarm.message}</p>
                <p className="text-xs text-text-muted">
                  {tagName.get(alarm.tagId) ?? alarm.tagId} ·{' '}
                  {formatRelativeTime(alarm.triggeredAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

/* ─── Live Chart ──────────────────────────────────────────────────────────── */

function LiveChartPanel() {
  const { data: tagsData } = useGetTagsQuery({
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })

  const chartTag = useMemo(
    () =>
      (tagsData?.items ?? []).find((t) => t.dataType === 'float' || t.dataType === 'integer'),
    [tagsData],
  )

  if (!chartTag) {
    return (
      <Card padding="md">
        <h2 className="mb-3 text-sm font-semibold text-text-primary">Live Value</h2>
        <EmptyState title="No tags" message="No numeric tags to display." />
      </Card>
    )
  }

  return <LiveChartCard tag={chartTag} />
}

function LiveChartCard({ tag }: { tag: Tag }) {
  const now = Date.now()
  const from = new Date(now - 60 * 60 * 1000).toISOString()
  const to = new Date(now).toISOString()

  const { data: history, isLoading } = useGetTagHistoryQuery(
    { id: tag.id, query: { from, to, limit: 500 } },
    { pollingInterval: TIME.TAG_REFRESH_MS },
  )

  const live = useLiveTagValue(tag.id)

  const chartData: ChartDataPoint[] = useMemo(() => {
    const historical = (history?.points ?? [])
      .filter((p): p is typeof p & { value: number } => p.value !== null)
      .map((p) => ({ time: new Date(p.timestamp).getTime(), value: p.value }))
    if (live.value !== null) {
      historical.push({ time: Date.now(), value: live.value })
    }
    return historical
  }, [history, live.value])

  return (
    <Card padding="md">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">
          {tag.name}
          {tag.unit ? ` (${tag.unit})` : ''}
        </h2>
        {live.value !== null && (
          <span className="font-mono text-lg font-semibold text-accent">
            {formatValue(live.value, tag.unit ?? undefined)}
          </span>
        )}
      </div>
      {isLoading ? (
        <Skeleton height={240} />
      ) : (
        <LineChart
          data={chartData}
          series={[{ dataKey: 'value', label: tag.name }]}
          xAxisKey="time"
          height={240}
          showGrid
          showTooltip
          isAnimationActive={false}
          emptyMessage="No data for the last hour."
        />
      )}
    </Card>
  )
}

/* ─── Gateway List ────────────────────────────────────────────────────────── */

function GatewayListPanel() {
  const navigate = useNavigate()
  const { data, isLoading } = useGetGatewaysQuery(undefined, {
    pollingInterval: TIME.ALARM_REFRESH_MS,
  })

  const gateways = data?.items ?? []

  return (
    <Card padding="md">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">Gateways</h2>
        <button
          type="button"
          onClick={() => navigate(ROUTES.GATEWAYS)}
          className="text-xs text-accent hover:underline"
        >
          View all
        </button>
      </div>

      {isLoading ? (
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={60} width="33%" />
          ))}
        </div>
      ) : gateways.length === 0 ? (
        <EmptyState title="No gateways" message="No gateways registered." />
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {gateways.map((gw) => (
            <GatewayCard key={gw.id} gateway={gw} />
          ))}
        </div>
      )}
    </Card>
  )
}

function GatewayCard({ gateway }: { gateway: Gateway }) {
  const navigate = useNavigate()
  const statusType =
    gateway.status === 'online' ? 'online' : gateway.status === 'error' ? 'error' : 'offline'

  return (
    <button
      type="button"
      onClick={() => navigate(`${ROUTES.GATEWAYS}/${gateway.id}`)}
      className="flex items-center gap-3 rounded-lg border border-border-subtle bg-bg-base px-3 py-2.5 text-left hover:border-accent/50"
    >
      <StatusDot status={statusType} pulse={statusType === 'online'} />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-text-primary">{gateway.name}</p>
        <p className="text-xs text-text-muted">
          {(gateway.protocolConfig as { protocol?: string } | null)?.protocol ?? '—'}
        </p>
      </div>
    </button>
  )
}
